<template>
  <section class="booking-history" aria-label="Booking history" :aria-busy="loading ? 'true' : 'false'">
    <p v-if="!manual" class="booking-history__empty">No manual booking history for this booking.</p>
    <template v-else>
      <app-block-loader v-if="loading && !events.length" min-height="180px" :rows="4">Loading booking history…</app-block-loader>
      <div v-if="error" class="booking-history__error" role="alert"><span>{{ error }}</span><app-button theme="dark" size="small" :disabled="loading" @click.native="load">Retry</app-button></div>
      <p v-if="!loading && !events.length && !error" class="booking-history__empty">No history yet.</p>
      <ol class="booking-history__list">
        <li v-for="event in events" :key="event.id" class="booking-history__card">
          <header class="booking-history__heading">
            <span class="booking-history__icon" :data-action="event.action.code" aria-hidden="true"><i class="pi" :class="eventIcon(event)" /></span>
            <div class="booking-history__identity">
              <div class="booking-history__title"><h3>{{ event.action.name }}</h3></div>
              <p class="booking-history__actor"><i class="pi pi-user" aria-hidden="true" />{{ actor(event.actor) }}</p>
            </div>
            <time class="booking-history__time" :datetime="event.occurred_at"><i class="pi pi-clock" aria-hidden="true" />{{ dateTime(event.occurred_at) }}</time>
          </header>
          <div class="booking-history__body">
            <dl class="booking-history__statuses">
              <div v-for="state in eventStatuses(event)" :key="state.key">
                <dt>{{ state.name }}</dt>
                <dd>
                  <template v-if="state.previous !== state.current"><span class="booking-history__badge booking-history__badge--previous">{{ statusLabel(state.previous) }}</span><span class="booking-history__arrow" aria-hidden="true">→</span></template>
                  <span class="booking-history__badge" :data-status="state.current">{{ statusLabel(state.current) }}</span>
                </dd>
              </div>
            </dl>
            <p v-if="event.comment" class="booking-history__comment"><i class="pi pi-comment" aria-hidden="true" /><span>{{ event.comment }}</span></p>
            <dl v-if="historyChanges(event.context).length" class="booking-history__changes">
              <div v-for="change in historyChanges(event.context)" :key="change.field"><dt>{{ change.label }}</dt><dd><span class="booking-history__before">{{ change.before }}</span><span class="booking-history__arrow" aria-hidden="true">→</span><span class="booking-history__after">{{ change.after }}</span></dd></div>
            </dl>
          </div>
        </li>
      </ol>
    </template>
  </section>
</template>

<script>
import { getFlightsManualBookingHistory } from '@/http'
import { bookingHistoryChanges } from './booking-edits'
import { code, dateTime, responseError } from './booking-detail'

export default {
  name: 'FlightBookingHistory',
  props: { booking: { type: Object, required: true }, passengersRefreshKey: { type: Number, default: 0 } },
  data() { return { events: [], loading: false, error: '', requestId: 0 } },
  computed: { manual() { return code(this.booking.booking_mode) === 'manual' } },
  watch: { passengersRefreshKey() { this.load() } },
  mounted() { this.load() },
  beforeDestroy() { this.requestId++ },
  methods: {
    dateTime,
    historyChanges: bookingHistoryChanges,
    isBookingHistory(event) {
      if ((code(event.action) || '').includes('offer')) return false
      const context = event.context || {}
      // Booking edits can reference a previously accepted offer without being offer events.
      return ['manual_patch', 'manual_action'].includes(context.source) || (!context.offer_id && !context.application_offer_id)
    },
    actor(value) { return value ? [value.first_name, value.last_name].filter(Boolean).join(' ') || `User #${value.id}` : 'System' },
    eventIcon(event) {
      const action = event.action.code || ''
      if (action.includes('ticket')) return 'pi-ticket'
      if (action === 'update_time_limits' || action === 'mark_booked_provisionally') return 'pi-clock'
      if (action === 'update_pnr') return 'pi-pencil'
      if (action === 'book') return 'pi-send'
      if (action.includes('cancel') || action === 'mark_not_available') return 'pi-ban'
      if (action === 'mark_booked') return 'pi-check-circle'
      return 'pi-history'
    },
    eventStatuses(event) {
      return [{ key: 'request_status', name: 'Sales request' }, { key: 'provider_status', name: 'Booking provider status' }, { key: 'booking_status', name: 'Booking status' }].map(field => ({
        ...field,
        previous: code((event[field.key] || {}).previous) || null,
        current: code((event[field.key] || {}).new) || null
      }))
    },
    statusLabel(value) { return String(value || '—').replace(/_/g, ' ') },
    async load() {
      if (!this.manual) return
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try { const { data } = await getFlightsManualBookingHistory(this.booking.id); if (requestId === this.requestId) this.events = data.filter(this.isBookingHistory).sort((a, b) => b.id - a.id) } catch (error) { if (requestId === this.requestId) this.error = responseError(error) } finally { if (requestId === this.requestId) this.loading = false }
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-history { color: #20241d; }
.booking-history__list { display: grid; gap: 14px; list-style: none; margin: 0 0 24px; padding: 0; }
.booking-history__card { min-width: 0; overflow: hidden; border: 1px solid $color-border; border-radius: 8px; background: #fff; box-shadow: 0 2px 4px rgba(32, 36, 29, .025); }
.booking-history__heading { display: flex; align-items: center; gap: 12px; padding: 12px 18px; border-bottom: 1px solid #e8e9e5; background: #fafafa; }
.booking-history__icon { display: grid; place-items: center; flex: 0 0 36px; height: 36px; border: 1px solid #e0e6d7; border-radius: 8px; background: #f0f6e7; color: #527b12; font-size: 17px; }
.booking-history__icon[data-action='attach_ticket'], .booking-history__icon[data-action='update_ticket'] { border-color: #d9e8f1; background: #edf5fa; color: #3489bd; }
.booking-history__icon[data-action='update_time_limits'], .booking-history__icon[data-action='mark_booked_provisionally'] { border-color: #eee3cc; background: #fff8e9; color: #a17b24; }
.booking-history__identity { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 16px; min-width: 0; flex: 1; }
.booking-history__title { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.booking-history__title h3 { margin: 0; font-size: 15px; font-weight: 500; line-height: 1.4; overflow-wrap: anywhere; }
.booking-history__actor, .booking-history__time { display: flex; align-items: center; gap: 6px; color: $color-text-second; font-size: 12px; line-height: 1.5; }
.booking-history__actor { margin: 0; overflow-wrap: anywhere; }
.booking-history__actor .pi, .booking-history__time .pi { flex-shrink: 0; font-size: 11px; color: #8a8f84; }
.booking-history__time { flex-shrink: 0; font-variant-numeric: tabular-nums; }
.booking-history__body { padding: 16px 18px; }
.booking-history__statuses { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 20px; margin: 0; }
.booking-history__statuses > div { min-width: 0; }
.booking-history dt { color: $color-text-second; font-size: 12px; line-height: 1.5; }
.booking-history dd { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin: 6px 0 0; min-width: 0; line-height: 1.5; }
.booking-history__badge { padding: 3px 8px; border: 1px solid #e0e3da; border-radius: 5px; background: #f7f8f5; color: #62685d; font-size: 12px; overflow-wrap: anywhere; }
.booking-history__badge[data-status='book'], .booking-history__badge[data-status='booked'], .booking-history__badge[data-status='confirmed'] { border-color: #dce8ce; background: #f0f6e7; color: #527b12; }
.booking-history__badge[data-status='ticketed'] { border-color: #d9e8f1; background: #edf5fa; color: #3489bd; }
.booking-history__badge[data-status='provisional'], .booking-history__badge[data-status='held'], .booking-history__badge[data-status='booked_provisionally'] { border-color: #eee3cc; background: #fff8e9; color: #a17b24; }
.booking-history__badge[data-status='not_available'], .booking-history__badge[data-status='cancelled'], .booking-history__badge[data-status='cancel'] { border-color: #efd4d1; background: #fff5f3; color: #993c35; }
.booking-history__badge--previous { color: #7b8175; background: #fff; border-style: dashed; }
.booking-history__arrow { color: #939a8b; font-size: 14px; }
.booking-history__changes { display: grid; gap: 12px; margin: 16px 0 0; padding: 14px 16px; border: 1px solid #e6e9e0; border-radius: 6px; background: #fafbf8; }
.booking-history__changes dd { font-size: 14px; }
.booking-history__before, .booking-history__after { min-width: 0; overflow-wrap: anywhere; }
.booking-history__before { color: #7b8175; }
.booking-history__after { font-weight: 500; }
.booking-history__comment { display: flex; align-items: flex-start; gap: 8px; margin: 16px 0 0; color: $color-text-second; font-size: 13px; line-height: 1.5; }
.booking-history__comment .pi { flex-shrink: 0; margin-top: 3px; color: #8a8f84; }
.booking-history__comment span { min-width: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
.booking-history__empty { color: $color-text-second; font-size: 13px; }
.booking-history__error { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; color: $color-error; }
@media (max-width: 700px) {
  .booking-history__heading { flex-wrap: wrap; padding: 14px; }
  .booking-history__time { flex-basis: calc(100% - 48px); margin-left: 48px; min-width: 0; }
  .booking-history__body { padding: 14px; }
  .booking-history__statuses { grid-template-columns: minmax(0, 1fr); }
  .booking-history__statuses > div { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 12px; }
  .booking-history__statuses dd { margin: 0; }
  .booking-history__changes { padding: 12px; }
}
</style>
